import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useBusiness } from "@/hooks/use-business";
import { useToast } from "@/hooks/use-toast";

export type OrderMenu = {
  id: string;
  business_id: string;
  name: string;
  slug: string;
  position: number;
  is_default: boolean;
  active: boolean;
  created_at?: string;
  updated_at?: string;
};

const slugFromName = (name: string): string =>
  name
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "") || "menu";

export function useOrderMenus() {
  const { business } = useBusiness();
  const { toast } = useToast();
  const [menus, setMenus] = useState<OrderMenu[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchMenus = useCallback(async () => {
    if (!business) {
      setMenus([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data, error } = await supabase
      .from("order_menus" as any)
      .select("*")
      .eq("business_id", business.id)
      .order("is_default", { ascending: false })
      .order("position", { ascending: true })
      .order("name", { ascending: true });
    if (error) {
      setMenus([]);
      setLoading(false);
      return;
    }
    setMenus((data || []) as OrderMenu[]);
    setLoading(false);
  }, [business]);

  useEffect(() => {
    fetchMenus();
  }, [fetchMenus]);

  const createMenu = async (input: { name: string; slug?: string }) => {
    if (!business) throw new Error("No business found");
    const name = input.name.trim();
    if (!name) throw new Error("Menu name is required");
    const slug = (input.slug || slugFromName(name)).trim() || slugFromName(name);
    const isFirst = menus.length === 0;
    const { data, error } = await supabase
      .from("order_menus" as any)
      .insert({
        business_id: business.id,
        name,
        slug,
        position: menus.length + 1,
        is_default: isFirst,
        active: true,
      })
      .select()
      .single();
    if (error) throw error;
    toast({ title: "Menu created", description: `${name} added.` });
    await fetchMenus();
    return data as OrderMenu;
  };

  const updateMenu = async (
    id: string,
    patch: Partial<Pick<OrderMenu, "name" | "slug" | "position" | "active" | "is_default">>
  ) => {
    const { data, error } = await supabase
      .from("order_menus" as any)
      .update(patch)
      .eq("id", id)
      .select()
      .single();
    if (error) throw error;
    if (patch.is_default === true) {
      await supabase
        .from("order_menus" as any)
        .update({ is_default: false })
        .eq("business_id", (data as any).business_id)
        .neq("id", id);
    }
    toast({ title: "Menu updated" });
    await fetchMenus();
    return data as OrderMenu;
  };

  const setDefaultMenu = async (id: string) => {
    const menu = menus.find((m) => m.id === id);
    if (!menu) return;
    await updateMenu(id, { is_default: true });
  };

  const deleteMenu = async (id: string) => {
    const { error } = await supabase.from("order_menus" as any).delete().eq("id", id);
    if (error) throw error;
    toast({ title: "Menu deleted" });
    await fetchMenus();
  };

  return {
    menus,
    loading,
    refetch: fetchMenus,
    createMenu,
    updateMenu,
    setDefaultMenu,
    deleteMenu,
  };
}
